/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {Box, Button, Stack, Divider} from '@salesforce/retail-react-app/app/components/shared/ui'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import useUpdatePasswordFields from '../forms/useUpdatePasswordFields'
import Field from '../field'
import PasswordRequirements, {
    generatePasswordAriaDescribedByIds
} from '../forms/password-requirements'

/**
 * UpdatePasswordFields is a React functional component that renders a form section
 * for updating a user's password. It includes fields for the current password and
 * a new password, along with a link to reset the password if forgotten.
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.form - The form object used for managing form state and validation.
 * @param {string} [props.prefix=''] - An optional prefix string to be used for field names or IDs.
 *
 * @returns {JSX.Element} A JSX element containing the password update fields and associated UI components.
 */
const UpdatePasswordFields = ({form, prefix = 'update-pass_'}) => {
    const fields = useUpdatePasswordFields({form, prefix})
    const password = form.watch(`${prefix}password`)
    const navigate = useNavigation()
    const currentPasswordRef = useRef()

    useEffect(() => {
        if (currentPasswordRef.current) {
            currentPasswordRef.current.focus()
        }
    }, [])

    return (
        <Stack spacing={32}>
            <Stack spacing={24}>
                <Stack spacing={16}>
                    <Field {...fields.currentPassword} inputRef={currentPasswordRef} />
                    <Box>
                        <Button variant="link" onClick={() => navigate('/reset-password')}>
                            <FormattedMessage
                                defaultMessage="Forgot Password?"
                                id="update_password_fields.button.forgot_password"
                            />
                        </Button>
                    </Box>
                </Stack>
                <Divider color="borderGray" />
            </Stack>

            <Stack spacing={24}>
                <Field
                    {...fields.password}
                    inputProps={{
                        'aria-describedby': generatePasswordAriaDescribedByIds(prefix)
                    }}
                />
                <PasswordRequirements value={password} formPrefix={prefix} />
            </Stack>
        </Stack>
    )
}

UpdatePasswordFields.propTypes = {
    /** Object returned from `useForm` */
    form: PropTypes.object.isRequired,

    /** Optional prefix for field names */
    prefix: PropTypes.string
}

export default UpdatePasswordFields
